import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Spinner,
  // Tab,
  // TabList,
  // TabPanel,
  // TabPanels,
  // Tabs,
  Text,
} from "@chakra-ui/react";
import Tooltip from "@components/Tooltip";
import { CLAIM_CTA } from "@constants/analytics";
import { toFixedWithoutRounding } from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useWithdrawals from "@hooks/useWithdrawals";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { emitReduxEvent, trackEvent } from "utils";
import { Hash, formatEther } from "viem";
import { useAccount, useBlockNumber, useSwitchChain } from "wagmi";
import useTransactionHistory from "@hooks/useTransactionHistory";
import TnxHistory from "./TnxHistory";
import SubscribeEmail from "./SubscribeEmail";

const initialCurrentWithdraw = {
  asset: "",
  nonce: 0n,
};

const Withdraw = () => {
  const { data: blockNumber } = useBlockNumber({
    query: { refetchInterval: 10_000 },
  });
  const { isConnected } = useAccount();
  const [currentWithdraw, setCurrentWithdraw] = useState(
    initialCurrentWithdraw
  );

  const { switchChain } = useSwitchChain();
  const { openConnectModal } = useConnectModal();

  const { isWithdrawSupported, withdrawEnabledChains } = useDappConfig();
  //   const [tabIndex, setTabIndex] = useState(0);

  const {
    userWithdrawals,
    isFetching,
    firstAvailableWithdrawalNonce,
    refetchWithdrawals,
  } = useWithdrawals();

  const { completedWithdrawals, isFetching: isFetchingCompletedWithdrawals } =
    useTransactionHistory();

  /* Active / Completed tabs */
  const TABS = { active: "Active", completed: "Completed" };
  const [showCompleted, setShowCompleted] = useState(false);
  const handleTabToggle = (option: string) => {
    setShowCompleted(option === TABS.completed);
  };

  const handleClick = useCallback(
    (amount: number, asset: Hash, nonce: bigint) => {
      if (!isWithdrawSupported) {
        switchChain({ chainId: withdrawEnabledChains[0].id });
      } else {
        trackEvent(CLAIM_CTA, {
          amount: amount,
          asset: asset,
        });

        setCurrentWithdraw({ asset, nonce });

        emitReduxEvent(EVENTS.HANDLE_CLAIM, {
          amount,
          asset,
          callback: () => {
            setCurrentWithdraw(initialCurrentWithdraw);
            refetchWithdrawals();
          },
        });
      }
    },
    [
      isWithdrawSupported,
      refetchWithdrawals,
      switchChain,
      withdrawEnabledChains,
    ]
  );

  const renderPendingWithdrawal = () => {
    return (
      <Box>
        <Text
          fontSize={{ base: "12px", md: "14px" }}
          letterSpacing={"1px"}
          fontWeight="400"
          mb={6}
          py={2}
        >
          AMOUNT
        </Text>
        <Box maxH={398} overflowY="auto">
          {userWithdrawals
            .sort((a, b) => a.startTime - b.startTime)
            .map((withdrawal) => {
              const isClaimable = withdrawal.isUnlocked;

              const hasWithdrawDelayLapsed =
                withdrawal.totalWithdrawDelay < Number(blockNumber);

              const finalDuration =
                (withdrawal.unlockedOnBlockNumber - Number(blockNumber)) * 12;
              const initialDuration =
                (withdrawal.initialUnlockDay - Number(blockNumber)) * 12;

              const finalDurationText = moment.duration(
                finalDuration,
                "seconds"
              );
              const initialDurationText = moment.duration(
                initialDuration,
                "seconds"
              );

              const isDisabled =
                firstAvailableWithdrawalNonce[withdrawal.asset] !==
                withdrawal.nonce;

              const beingWithdrawn =
                currentWithdraw.asset === withdrawal.asset &&
                currentWithdraw.nonce === withdrawal.nonce;

              return (
                <Flex
                  mb={6}
                  key={`${withdrawal.asset}-${withdrawal.startTime}`}
                  justify="space-between"
                  alignItems="center"
                >
                  <Text fontSize={{ base: 14, lg: 16 }}>
                    {
                      +toFixedWithoutRounding(
                        formatEther(withdrawal.expectedAssetAmount),
                        6
                      )
                    }{" "}
                    {withdrawal.symbol}
                  </Text>
                  {isClaimable && hasWithdrawDelayLapsed && (
                    <Button
                      size="md"
                      w={20}
                      padding={"8px 16px"}
                      bg="textPrimary"
                      _hover={{
                        bg: "textPrimary",
                      }}
                      fontWeight="600"
                      color="backgroundCard"
                      fontSize={12}
                      isDisabled={isDisabled || beingWithdrawn}
                      onClick={() =>
                        !isDisabled
                          ? handleClick(
                              +toFixedWithoutRounding(
                                formatEther(withdrawal.expectedAssetAmount),
                                6
                              ),
                              withdrawal.asset,
                              withdrawal.nonce
                            )
                          : {}
                      }
                    >
                      {beingWithdrawn ? (
                        <Spinner size="md" color="primary" />
                      ) : (
                        <Flex alignItems="center" gap={1}>
                          Claim
                          {isDisabled && (
                            <Tooltip
                              label={
                                <Box>
                                  Please withdraw the available claim for{" "}
                                  {withdrawal.symbol} above first.
                                </Box>
                              }
                            />
                          )}
                        </Flex>
                      )}
                    </Button>
                  )}
                  {!(isClaimable && hasWithdrawDelayLapsed) && (
                    <Text fontSize={{ base: 14, lg: 16 }}>
                      {finalDurationText.days() <= 0
                        ? "awaiting unlock"
                        : `~in${" "}
                          ${
                            finalDurationText.days() > 1
                              ? `${
                                  initialDurationText.days() <= 0
                                    ? 1
                                    : initialDurationText.days()
                                } -`
                              : ""
                          }
                          ${finalDurationText.days()} day${
                            finalDurationText.days() > 1 ? "s" : ""
                          }`}
                    </Text>
                  )}
                </Flex>
              );
            })}
        </Box>
      </Box>
    );
  };

  const renderCompleteWithdrawals = useMemo(() => {
    return (
      <Box minH={200}>
        {isFetchingCompletedWithdrawals ? (
          <Center
            h={isWithdrawSupported ? 200 : 200}
            flexDir="column"
            textAlign="center"
            gap={4}
          >
            <Spinner size="md" color="primary" />
          </Center>
        ) : (
          <TnxHistory tnxData={completedWithdrawals} />
        )}
      </Box>
    );
  }, [
    completedWithdrawals,
    isFetchingCompletedWithdrawals,
    isWithdrawSupported,
  ]);

  const renderNoUnstakeRequest = (title?: string, desc?: string, h = 440) => {
    return (
      <Center
        h={isWithdrawSupported ? h : 336}
        flexDir="column"
        textAlign="center"
        gap={4}
      >
        {isFetching ? (
          <Spinner size="md" color="primary" />
        ) : (
          <>
            {title && (
              <Text fontSize={{ base: 18, lg: 24 }} fontWeight="600">
                {title}
              </Text>
            )}
            {desc && <Text fontSize={{ base: 14, lg: 16 }}>{desc}</Text>}
          </>
        )}
      </Center>
    );
  };

  const renderConnectWallet = () => {
    return (
      <Center
        h={isWithdrawSupported ? 440 : 336}
        flexDir="column"
        textAlign="center"
        gap={4}
      >
        <Text fontSize={24} fontWeight="600">
          Connect your wallet
        </Text>
        <Text>
          You will be able to claim your tokens after you connect your wallet
        </Text>
      </Center>
    );
  };

  const renderSwitchChainCta = () => {
    return (
      <Button
        borderRadius="12px"
        bg="textPrimary"
        p="16px 64px"
        h="56px"
        mt="12px"
        width="100%"
        _hover={{
          bg: "textPrimary",
        }}
        onClick={() => switchChain({ chainId: withdrawEnabledChains[0].id })}
      >
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="600"
          color="backgroundCard"
        >
          {`Switch to ${withdrawEnabledChains[0].name} to Withdraw`}
        </Text>
      </Button>
    );
  };

  const renderConnectWalletCta = () => {
    return (
      <Button
        borderRadius="12px"
        bg="textPrimary"
        p="16px 64px"
        h="56px"
        mt="12px"
        width="100%"
        _hover={{
          bg: "textPrimary",
        }}
        onClick={openConnectModal}
      >
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="600"
          color="backgroundCard"
        >
          Connect Wallet
        </Text>
      </Button>
    );
  };

  const renderSubscribeEmail = () => {
    return (
      <>
        <Divider
          color="textPrimary"
          my={{ base: "16px", md: "24px" }}
          h="1px"
          opacity="0.8"
          borderColor="#B6DFDF"
        />
        <Flex
          direction="column"
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Flex>
            <Text
              fontSize={"12px"}
              lineHeight={"20px"}
              fontWeight={400}
              mb={4}
              textAlign={"center"}
            >
              Sign up with your email to learn about upcoming vaults & DeFi
              opportunities.
            </Text>
          </Flex>
          <Flex>
            <SubscribeEmail />
          </Flex>
        </Flex>
      </>
    );
  };

  const renderWithdrawals = () => {
    if (isConnected && !showCompleted && userWithdrawals.length) {
      return renderPendingWithdrawal();
    }
    if (isConnected && showCompleted && completedWithdrawals.length) {
      return renderCompleteWithdrawals;
    }
    if (isConnected) {
      if (!completedWithdrawals.length) {
        return renderNoUnstakeRequest("No withdrawals yet", undefined, 150);
      } else {
        return renderNoUnstakeRequest(
          "No unstake requests found",
          "You will be able to claim your tokens after the Unstake request has been processed. To Unstake your tokens go to Unstake tab"
        );
      }
    }
    return renderConnectWallet();
  };

  const renderActiveCompletedTabs = () => {
    return (
      <Box pb={10}>
        <Flex
          border="1px solid"
          borderColor="#D8F0F0"
          borderRadius="12px"
          overflow="hidden"
          w="fit-content"
          position="relative"
          p={"4px 8px 4px 8px"}
        >
          <Box
            position="absolute"
            top="0"
            bottom="0"
            left={showCompleted ? "40%" : "0"}
            right={showCompleted ? "0" : "55%"}
            bg="textPrimary"
            borderRadius="8px"
            transition="all 0.2s ease"
            m={1}
          />

          <Button
            onClick={() => handleTabToggle(TABS.active)}
            zIndex={1}
            color={!showCompleted ? "white" : "textPrimary"}
            borderRadius="8px"
            px={4}
            _hover={{ bg: "transparent" }}
            bg="transparent"
          >
            {TABS.active}
          </Button>

          <Button
            onClick={() => handleTabToggle(TABS.completed)}
            zIndex={1}
            color={showCompleted ? "white" : "textPrimary"}
            borderRadius="8px"
            px={4}
            _hover={{ bg: "transparent" }}
            bg="transparent"
          >
            {TABS.completed}
          </Button>
        </Flex>
      </Box>
    );
  };
  return (
    <Box pos="relative">
      <Box
        pos="relative"
        pointerEvents={isWithdrawSupported || !isConnected ? "all" : "none"}
        filter="auto"
        blur={isWithdrawSupported || !isConnected ? "0" : "3px"}
      >
        {isConnected &&
          isWithdrawSupported &&
          !isFetchingCompletedWithdrawals &&
          completedWithdrawals.length > 0 &&
          renderActiveCompletedTabs()}
        {/* Refactored code with exiting logic */}
        {renderWithdrawals()}
      </Box>
      {!isWithdrawSupported && isConnected && renderSwitchChainCta()}
      {!isConnected && renderConnectWalletCta()}
      {isConnected && isWithdrawSupported && renderSubscribeEmail()}
    </Box>
  );
};

export default Withdraw;
